// src/App.js
import React, { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import Header from './components/common/header/Header.js';
import AnnouncementPopup from './components/common/announcements/AnnouncementPopup';
import OpenInAppBanner from './components/common/OpenInAppBanner';
import ArtistDemoBanner from './components/common/manuals/ArtistDemoBanner';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { useArtistAuth } from './contexts/ArtistAuthContext';
import { useMaintenance } from './contexts/MaintenanceContext';
import { hasExactAdminRole, resolveAdminPermission } from './constants/adminPermissionCatalog';
import MaintenanceNotice from './components/common/maintenance/MaintenanceNotice';
import MaintenanceBanner from './components/common/maintenance/MaintenanceBanner';
import ConnectivityBanner from './components/common/ConnectivityBanner';
import ArtistOnboardingGate from './components/artist/onboarding/ArtistOnboardingGate';

/* ========== Login / Setup ========== */
import SiteFooter from './components/common/footer/SiteFooter';
import ResponsiveFooterNav from './components/common/footer/ResponsiveFooterNav';
import AdminFullNameRequiredModal from './components/admin/settings/AdminFullNameRequiredModal.js';
import AdminWorkAvailabilityPrompt from './components/common/workAvailability/AdminWorkAvailabilityPrompt';
import AdminQuickTaskGlobalLauncher from './components/admin/tasks/AdminQuickTaskGlobalLauncher.jsx';
import AdminChat from './components/admin/chats/ChatPage.js';
import AdminGroupChatPage from './components/admin/chats/AdminGroupChatPage.js';
import AdminArtistBroadcastPage from './components/admin/chats/AdminArtistBroadcastPage.js';
import EventList from './components/admin/events/EventList.js';
import EventDetails from './components/admin/events/EventDetails.js';
import EventChatPage from './components/admin/chats/EventChatPage.js';
import useSelectionSafeNavigation from './hooks/common/useSelectionSafeNavigation';
import TimetableViewerPage from './components/common/events/TimetableViewerPage.js';
import ArtistEventList from './components/artist/events/ArtistEventList.js';
import ArtistEventDetails from './components/artist/events/ArtistEventDetails.js';
import ArtistEventChatPage from './components/artist/chats/ArtistEventChatPage.js';
import ArtistChat from './components/artist/chats/ArtistChatPage.js';
import PeerToastStack from './components/common/PeerToastStack';
import { MessageCountProvider } from './contexts/MessageCountContext';
import { ResponseStatusProvider } from './components/common/workAvailability/ResponseStatusProvider';
import { AdminMenuBadgeProvider } from './contexts/AdminMenuBadgeContext';
import { OfferCountProvider } from './contexts/OfferCountContext';
import { ContractCountProvider } from './contexts/ContractCountContext';
import GlobalSocketListener from './components/common/GlobalSocketListener';
import { PdfPreviewProvider } from './components/common/pdf/PdfPreviewProvider';

const HomePage = lazy(() => import('./components/public/HomePage'));
const PolicyPage = lazy(() => import('./components/public/PolicyPage'));
const ConciergeExternalApplyPage = lazy(() => import('./components/public/concierge/ConciergeExternalApplyPage'));
const ConciergeStatusPage = lazy(() => import('./components/public/concierge/ConciergeStatusPage'));
const ServiceGuidePage = lazy(() => import('./components/public/serviceGuide/ServiceGuidePage'));
const PublicStagePreviewPage = lazy(() => import('./components/public/stage/PublicStagePreviewPage'));
const PublicSurveyPage = lazy(() => import('./components/public/surveys/PublicSurveyPage'));
const AdminLogin = lazy(() => import('./components/login/AdminLogin'));
const AdminSetup = lazy(() => import('./components/login/AdminSetup'));
const AdminForgotPassword = lazy(() => import('./components/login/AdminForgotPassword.js'));
const AdminResetPassword = lazy(() => import('./components/login/AdminResetPassword.js'));
const ArtistLogin = lazy(() => import('./components/login/ArtistLogin'));
const ArtistSetup = lazy(() => import('./components/login/ArtistSetup'));
const ArtistLimitedInviteGate = lazy(() => import('./components/login/ArtistLimitedInviteGate'));
const ArtistSignupFull = lazy(() => import('./components/login/ArtistSignupFull'));
const ArtistForgotPassword = lazy(() => import('./components/login/ArtistForgotPassword'));
const ArtistResetPassword = lazy(() => import('./components/login/ArtistResetPassword'));
const ArtistEmailChangeConfirm = lazy(() => import('./components/login/ArtistEmailChangeConfirm.js'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const AdminAccountManagement = lazy(() => import('./components/admin/settings/AdminAccountManagement.js'));
const AdminProfileEdit = lazy(() => import('./components/admin/settings/AdminProfileEdit.js'));
const AdminNotificationSettings = lazy(() => import('./components/admin/settings/AdminNotificationSettings.js'));
const AdminWorkAvailabilityPage = lazy(() => import('./components/admin/settings/AdminWorkAvailabilityPage'));
const AdminChangePassword = lazy(() => import('./components/admin/settings/AdminChangePassword.js'));
const AdminManualsPage = lazy(() => import('./components/admin/settings/AdminManualsPage.js'));
const AnnouncementManagement = lazy(() => import('./components/admin/system/AnnouncementManagement'));
const AdminOperationalEmailPage = lazy(() => import('./components/admin/system/AdminOperationalEmailPage'));
const AdminBugManagement = lazy(() => import('./components/admin/system/AdminBugManagement'));
const MobileReleaseManagement = lazy(() => import('./components/admin/system/MobileReleaseManagement'));
const MacReleaseManagement = lazy(() => import('./components/admin/system/MacReleaseManagement'));
const PolicyEditor = lazy(() => import('./components/admin/system/PolicyEditor'));
const ContractClauseSettingsPage = lazy(() => import('./components/admin/system/ContractClauseSettingsPage'));
const AdminActionLogPage = lazy(() => import('./components/admin/system/AdminActionLogPage'));
const PlayguideConnectionSettings = lazy(() => import('./components/admin/system/PlayguideConnectionSettings'));
const AdminInvoicePreview = lazy(() => import('./components/admin/finances/AdminInvoicePreview.js'));
const AdminFinanceDashboard = lazy(() => import('./components/admin/finances/AdminFinaceDashboard.js'));
const AdminServiceAiPage = lazy(() => import('./components/admin/serviceAi/AdminServiceAiPage'));
const AdminServiceAiSettingsPage = lazy(() => import('./components/admin/serviceAi/AdminServiceAiSettingsPage'));
const AdminServiceAiConversationReviewPage = lazy(() => import('./components/admin/serviceAi/AdminServiceAiConversationReviewPage'));
const AdminServiceAiUsageLogPage = lazy(() => import('./components/admin/serviceAi/AdminServiceAiUsageLogPage'));
const AdminSurveyImportsPage = lazy(() => import('./components/admin/surveys/AdminSurveyImportsPage'));
const DigitalSurveyOnboardingMockPage = process.env.NODE_ENV !== 'production'
  ? lazy(() => import('./components/admin/surveys/DigitalSurveyOnboardingMockPage'))
  : null;
const AdminTaskHome = lazy(() => import('./components/admin/tasks/AdminTaskHome'));
const AdminQuickTaskListPage = lazy(() => import('./components/admin/tasks/AdminQuickTaskListPage'));
const AdminTaskProjectDetail = lazy(() => import('./components/admin/tasks/AdminTaskProjectDetail'));
const AdminTaskEventList = lazy(() => import('./components/admin/tasks/AdminTaskEventList'));
const AdminTaskTemplateCreatePage = lazy(() => import('./components/admin/tasks/AdminTaskTemplateCreatePage'));
const ArtistRegister = lazy(() => import('./components/admin/artists/ArtistRegister.js'));
const ArtistRegistrationComplete = lazy(() => import('./components/admin/artists/ArtistRegistrationComplete.js'));
const ArtistList = lazy(() => import('./components/admin/artists/ArtistList.js'));
const ArtistDetail = lazy(() => import('./components/admin/artists/ArtistDetail.js'));
const ArtistGroups = lazy(() => import('./components/admin/artists/ArtistGroups.js'));
const EventRegistration = lazy(() => import('./components/admin/events/EventRegistration.js'));
const EventRegistrationComplete = lazy(() => import('./components/admin/events/EventRegistrationComplete.js'));
const EventCastingRegistration = lazy(() => import('./components/admin/events/EventCastingRegistration.js'));
const EventEdit = lazy(() => import('./components/admin/events/EventEdit.js'));
const EventOptionEdit = lazy(() => import('./components/admin/events/EventOptionEdit.js'));
const EventGenreManagement = lazy(() => import('./components/admin/events/EventGenreManagement.js'));
const EventParticipantInfo = lazy(() => import('./components/admin/events/EventParticipantInfo.js'));
const EventTicketSales = lazy(() => import('./components/admin/events/EventTicketSales.js'));
const EventTicketSalesAnalytics = lazy(() => import('./components/admin/events/EventTicketSalesAnalytics.js'));
const AdminEventMarketingPage = lazy(() => import('./components/admin/events/AdminEventMarketingPage.jsx'));
const EventProfitLoss = lazy(() => import('./components/admin/events/EventProfitLoss.js'));
const EventTimetableDesigner = lazy(() => import('./components/admin/events/EventTimetableDesigner.js'));
const EventStageSeatingPlanner = lazy(() => import('./components/admin/events/EventStageSeatingPlanner.js'));
const TicketSalesOverview = lazy(() => import('./components/admin/events/TicketSalesOverview.js'));
const CastingProgress = lazy(() => import('./components/admin/events/CastingProgress.js'));
const EventCastingWorkbench = lazy(() => import('./components/admin/events/EventCastingWorkbench'));
const MusicLibraryManagement = lazy(() => import('./components/admin/music/MusicLibraryManagement.jsx'));
const AdminConciergeList = lazy(() => import('./components/admin/concierge/AdminConciergeList'));
const PartsEdit = lazy(() => import('./components/admin/artists/PartEdit'));
const GroupTagEdit = lazy(() => import('./components/admin/artists/GroupTagEdit'));
const VenueList = lazy(() => import('./components/admin/venues/VenueList.js'));
const VenueRegistration = lazy(() => import('./components/admin/venues/VenueRegistration.js'));
const VenueDetail = lazy(() => import('./components/admin/venues/VenueDetail.js'));
const HoldCasting = lazy(() => import('./components/admin/castings/offers/OfferForm.js'));
const HoldCastingHistory = lazy(() => import('./components/admin/castings/offers/OfferHistory.js'));
const HoldCastingDetail = lazy(() => import('./components/admin/castings/offers/OfferDetail.js'));
const ContractForm = lazy(() => import('./components/admin/castings/contracts/ContractForm.js'));
const ContractConfirm = lazy(() => import('./components/admin/castings/contracts/ContractConfirm.js'));
const ContractSuccess = lazy(() => import('./components/admin/castings/contracts/ContractSuccess.js'));
const ContractHistory = lazy(() => import('./components/admin/castings/contracts/ContractHistory.js'));
const ContractDetail = lazy(() => import('./components/admin/castings/contracts/ContractDetail.js'));
const AdditionalRewardBatchPage = lazy(() => import('./components/admin/castings/contracts/AdditionalRewardBatchPage.tsx'));
const EventContractScheduleBatchPage = lazy(() => import('./components/admin/castings/contracts/EventContractScheduleBatchPage.tsx'));
const ContractEdit = lazy(() => import('./components/admin/castings/contracts/ContractEdit.js'));
const ContractEditConfirmPersonal = lazy(() => import('./components/admin/castings/contracts/ContractEditConfirmPersonal.js'));
const ContractEditConfirmCommon = lazy(() => import('./components/admin/castings/contracts/ContractEditConfirmCommon.js'));
const MonthlyPaymentsList = lazy(() => import('./components/admin/finances/MonthlyPaymentsList.js'));
const ArtistDashboard = lazy(() => import('./components/artist/ArtistDashboard'));
const ArtistGuestTicketsPage = lazy(() => import('./components/artist/events/ArtistGuestTicketsPage'));
const ArtistContractHistory = lazy(() => import('./components/artist/castings/ArtistContractList.js'));
const ArtistContractDetail = lazy(() => import('./components/artist/castings/ArtistContractDetail.js'));
const PrintContract = lazy(() => import('./components/artist/castings/ArtistPrintContract.js'));
const ArtistOfferDetail = lazy(() => import('./components/artist/castings/ArtistOfferDetails.js'));
const ArtistOffers = lazy(() => import('./components/artist/castings/ArtistOfferList.js'));
const ArtistVenueDetail = lazy(() => import('./components/artist/venues/ArtistVenueDetail'));
const ArtistProfileEdit = lazy(() => import('./components/artist/settings/ArtistProfileEdit.js'));
const ArtistManualsPage = lazy(() => import('./components/artist/settings/ArtistManualsPage.js'));
const ArtistBankInfo = lazy(() => import('./components/artist/settings/ArtistBankInfo.js'));
const ArtistChangePassword = lazy(() => import('./components/artist/settings/ArtistChangePassword.js'));
const ArtistNotificationSettings = lazy(() => import('./components/artist/settings/ArtistNotificationSettings.js'));
const ArtistPhotography = lazy(() => import('./components/artist/settings/ArtistPhotography'));
const ArtistRewardsList = lazy(() => import('./components/artist/finances/ArtistRewardsList.js'));
const ArtistInvoiceMakerPage = lazy(() => import('./components/artist/finances/ArtistInvoiceMakerPage.js'));
const ArtistBugReportPage = lazy(() => import('./components/artist/BugReportPage'));
const ArtistEmailChange = lazy(() => import('./components/artist/settings/ArtistEmailChange.js'));
const ArtistConciergeDashboard = lazy(() => import('./components/artist/concierge/ArtistConciergeDashboard'));
const ArtistConciergeApplyForm = lazy(() => import('./components/artist/concierge/ArtistConciergeApplyForm'));
const ArtistConciergeAdditionalInfoPage = lazy(() => import('./components/artist/concierge/ArtistConciergeAdditionalInfoPage'));
const DevPortalApp = lazy(() => import('./components/dev/DevPortalApp'));

const SR_ONLY_STATUS_STYLE = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0
};

const AuthGateScreen = () => (
  <span style={SR_ONLY_STATUS_STYLE} role="status" aria-live="polite">
    Checking session...
  </span>
);

const SESSION_UNAVAILABLE_GATE_STYLE = {
  minHeight: '100dvh',
  display: 'grid',
  placeItems: 'center',
  padding: '24px',
  background: 'var(--color-surface-page)',
  color: 'var(--color-text)',
};

const SESSION_UNAVAILABLE_GATE_PANEL_STYLE = {
  width: 'min(480px, 100%)',
  padding: '20px',
  borderRadius: '8px',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-sm)',
};

const SESSION_UNAVAILABLE_GATE_TITLE_STYLE = {
  margin: '0 0 8px',
  fontSize: '18px',
  lineHeight: 1.4,
};

const SESSION_UNAVAILABLE_GATE_BODY_STYLE = {
  margin: 0,
  color: 'var(--color-muted)',
  fontSize: '14px',
  lineHeight: 1.7,
};

const SessionUnavailableAuthGateScreen = () => (
  <main style={SESSION_UNAVAILABLE_GATE_STYLE} role="status" aria-live="polite">
    <section style={SESSION_UNAVAILABLE_GATE_PANEL_STYLE}>
      <h1 style={SESSION_UNAVAILABLE_GATE_TITLE_STYLE}>通信の回復を待っています</h1>
      <p style={SESSION_UNAVAILABLE_GATE_BODY_STYLE}>
        ログアウトはしていません。現在のページを維持したまま、サーバーとの再接続を確認しています。
      </p>
    </section>
  </main>
);

const NativeStageSeatingAuthBlockedScreen = () => (
  <main className="stage-seating-native-auth-blocked">
    <div className="stage-seating-native-auth-blocked__card">
      <p className="stage-seating-native-auth-blocked__eyebrow">macOS認証を確認できません</p>
      <h1 className="stage-seating-native-auth-blocked__title">シーティングを開けませんでした</h1>
      <p className="stage-seating-native-auth-blocked__body">
        macOSアプリ側のログイン状態を再確認してから、イベント詳細のシーティングを開き直してください。
      </p>
    </div>
  </main>
);

const isNativeStageSeatingRoute = (location) => {
  if (typeof window === 'undefined') return false;
  const path = String(location?.pathname || '');
  const isStageSeatingPath =
    (path.startsWith('/admin/workspaces/event-operations/events/') || path.startsWith('/artist/workspaces/event-operations/events/')) &&
    path.includes('/stage-seating');
  if (!isStageSeatingPath) return false;
  const userAgent = String(window.navigator?.userAgent || '');
  return Boolean(
    window.__CASTICART_NATIVE_AUTH__ ||
      window.webkit?.messageHandlers?.casticartStageRoute ||
      /Casticart(Admin)?Mac/i.test(userAgent)
  );
};

const hasNativeStageSeatingAuthorization = () => {
  if (typeof window === 'undefined') return false;
  const bridge = window.__CASTICART_NATIVE_AUTH__;
  const authorization = String(
    bridge?.authorizationHeader || bridge?.authorization || bridge?.Authorization || ''
  ).trim();
  return /^Bearer\s+\S+/i.test(authorization);
};

const canUseNativeStageSeatingSession = (location) =>
  isNativeStageSeatingRoute(location) && hasNativeStageSeatingAuthorization();

const TASK_ONLY_ALLOWED_ADMIN_PATHS = [
  '/admin/workspaces/task-management/dashboard',
  '/admin/settings/profile',
  '/admin/settings/change-password',
  '/admin/settings/notifications',
  '/admin/settings/work-availability',
];

const isAdminEventDetailPath = (path) =>
  /^\/admin\/workspaces\/event-operations\/events\/[^/]+\/?$/.test(path);

const isTaskOnlyAllowedPath = (pathname) => {
  const path = String(pathname || '').trim();
  if (!path) return false;
  if (path === '/admin/workspaces/event-operations/dashboard') return true;
  if (TASK_ONLY_ALLOWED_ADMIN_PATHS.includes(path)) return true;
  if (path.startsWith('/admin/workspaces/task-management/')) return true;
  if (isAdminEventDetailPath(path)) return true;
  return false;
};

const ADMIN_EVENT_DASHBOARD_PATH = '/admin/workspaces/event-operations/dashboard';
const ADMIN_TASK_DASHBOARD_PATH = '/admin/workspaces/task-management/dashboard';
const ARTIST_DASHBOARD_PATH = '/artist/workspaces/event-operations/dashboard';

// ロールに応じた「戻り先ダッシュボード」。task_only はタスク管理ホームが自分の起点。
const resolveAdminHomePath = (user) =>
  user?.role === 'task_only' ? ADMIN_TASK_DASHBOARD_PATH : ADMIN_EVENT_DASHBOARD_PATH;

// 未定義ルート/権限不足ルートに入ったときに、トースト通知を出しつつ指定先へ自動遷移させる共有コンポーネント。
const RedirectWithNotice = ({ to, message, toastType = 'info' }) => {
  const navigate = useNavigate();
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    if (typeof window !== 'undefined' && message) {
      window.dispatchEvent(
        new CustomEvent('app:toast', { detail: { type: toastType, text: message } })
      );
    }
    navigate(to, { replace: true });
  }, [navigate, to, message, toastType]);

  return <AuthGateScreen />;
};

const AdminPermissionBlockedPage = ({ title = 'このページは閲覧できません', description = 'このアカウントではこの画面を利用できません。' }) => (
  <div className="admin-dashboard">
    <div className="dashboard-main">
      <div className="access-block-card">
        <h2 className="access-block-card__title">{title}</h2>
        <p className="access-block-card__description">
          {description}
        </p>
        <Link
          to="/admin/workspaces/event-operations/dashboard"
          className="access-block-card__link"
        >
          ダッシュボードへ戻る
        </Link>
      </div>
    </div>
  </div>
);

const QueryRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const adminSetupToken = params.get('adminSetupToken');
    if (adminSetupToken && !location.pathname.startsWith('/admin-setup/')) {
      navigate(`/admin-setup/${adminSetupToken}`, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

const RequireAdminAuth = ({ children }) => {
  const { user, checking: adminChecking, sessionUnavailable } = useAuth();
  const { maintenance, maintenanceLoading, isMaintenanceActive, maintenanceMode, isRoleAllowed } = useMaintenance();
  const location = useLocation();
  const isReadOnly = maintenanceMode === 'read_only' || maintenance?.readOnly;
  const nativeStageSeatingSession = canUseNativeStageSeatingSession(location);

  if (!user && nativeStageSeatingSession) return children;
  if (adminChecking || maintenanceLoading) return <AuthGateScreen />;
  if (!user && sessionUnavailable) return <SessionUnavailableAuthGateScreen />;
  if (!user) {
    if (isNativeStageSeatingRoute(location)) {
      return <NativeStageSeatingAuthBlockedScreen />;
    }
    return <Navigate to="/admin-login" replace state={{ from: location }} />;
  }
  if (user?.role === 'task_only') {
    const path = location.pathname || '';
    if (path === ADMIN_EVENT_DASHBOARD_PATH) {
      return <Navigate to={ADMIN_TASK_DASHBOARD_PATH} replace />;
    }
    if (!isTaskOnlyAllowedPath(path)) {
      return (
        <RedirectWithNotice
          to={ADMIN_TASK_DASHBOARD_PATH}
          toastType="error"
          message="タスク管理専用アカウントではこのページを表示できません。タスク管理ホームに移動しました。"
        />
      );
    }
  }
  // 読み取り専用モードはログイン後のダッシュボード利用を許可する
  if (isReadOnly) {
    return children;
  }
  if (isMaintenanceActive && !isRoleAllowed('admin')) {
    return (
      <Navigate
        to="/maintenance/admin"
        replace
        state={{ from: location, maintenance, role: 'admin' }}
      />
    );
  }
  return children;
};

const RequireAdminPermission = ({ permissionKeys, permissionGroups, mode = 'any', children }) => {
  const { user, checking: adminChecking, sessionUnavailable } = useAuth();
  const location = useLocation();
  const nativeStageSeatingSession = canUseNativeStageSeatingSession(location);

  if (!user && nativeStageSeatingSession) return children;
  if (adminChecking) return <AuthGateScreen />;
  if (!user && sessionUnavailable) return <SessionUnavailableAuthGateScreen />;
  if (!user) {
    if (isNativeStageSeatingRoute(location)) {
      return <NativeStageSeatingAuthBlockedScreen />;
    }
    return <Navigate to="/admin-login" replace state={{ from: location }} />;
  }

  const keys = Array.isArray(permissionKeys)
    ? permissionKeys.filter(Boolean)
    : permissionKeys
      ? [permissionKeys]
      : [];
  const groups = Array.isArray(permissionGroups)
    ? permissionGroups
        .map((group) => (Array.isArray(group) ? group.filter(Boolean) : group ? [group] : []))
        .filter((group) => group.length > 0)
    : [];
  if (!keys.length && !groups.length) return children;

  const can = (permissionKey) =>
    resolveAdminPermission({
      role: user?.role || 'general',
      isExternalAccount: user?.is_external_account === true,
      permissions: Array.isArray(user?.permissions) ? user.permissions : [],
      permissionKey,
    });

  const isAllowed =
    groups.length > 0
      ? groups.every((group) => group.some((permissionKey) => can(permissionKey)))
      : keys[mode === 'all' ? 'every' : 'some']((permissionKey) => can(permissionKey));

  if (!isAllowed) {
    const target = resolveAdminHomePath(user);
    // 戻り先ダッシュボード自体が権限不足の場合（例: NAV_TASKS を持たない task_only）は
    // 無限リダイレクトになるため、そのときだけ終端の閲覧不可ページを表示する。
    if ((location.pathname || '') === target) {
      return (
        <AdminPermissionBlockedPage description="この管理者アカウントには必要な権限が付与されていません。" />
      );
    }
    return (
      <RedirectWithNotice
        to={target}
        toastType="error"
        message="このページを表示する権限がありません。ダッシュボードに移動しました。"
      />
    );
  }

  return children;
};

const RequireExactAdminRole = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!hasExactAdminRole(user?.role)) {
    const target = resolveAdminHomePath(user);
    if ((location.pathname || '') === target) {
      return (
        <AdminPermissionBlockedPage description="この設定はマスター権限を持つ管理者だけが変更できます。" />
      );
    }
    return (
      <RedirectWithNotice
        to={target}
        toastType="error"
        message="この操作にはマスター権限が必要です。ダッシュボードに移動しました。"
      />
    );
  }
  return children;
};

const RequireArtistAuth = ({ children }) => {
  const { artistUser, checking: artistChecking, sessionUnavailable, isArtistDemo, artistDemoScenario } = useArtistAuth();
  const { maintenance, maintenanceLoading, isMaintenanceActive, maintenanceMode, isRoleAllowed } = useMaintenance();
  const location = useLocation();
  const isReadOnly = maintenanceMode === 'read_only' || maintenance?.readOnly;
  const nativeStageSeatingSession = canUseNativeStageSeatingSession(location);

  if (!artistUser && nativeStageSeatingSession) return children;
  if (artistChecking || maintenanceLoading) return <AuthGateScreen />;
  if (!artistUser && sessionUnavailable) return <SessionUnavailableAuthGateScreen />;
  if (!artistUser) {
    if (isNativeStageSeatingRoute(location)) {
      return <NativeStageSeatingAuthBlockedScreen />;
    }
    const redirectTo = `${location.pathname || ''}${location.search || ''}` || '/artist/workspaces/event-operations/dashboard';
    try { sessionStorage.setItem('post_login_redirect', redirectTo); } catch {}
    return <Navigate to="/artist-login" replace state={{ redirectTo, from: location }} />;
  }
  if (isArtistDemo) {
    // 初回利用シナリオのデモでは、本番同様に初期設定ゲート（初回モーダル）を表示する。
    // それ以外のデモシナリオは従来どおりゲートを介さずダッシュボードへ。
    return artistDemoScenario === 'new-user'
      ? <ArtistOnboardingGate>{children}</ArtistOnboardingGate>
      : children;
  }
  // 読み取り専用モードはログイン後のダッシュボード利用を許可する
  if (isReadOnly) {
    return children;
  }
  if (isMaintenanceActive && !isRoleAllowed('artist')) {
    return (
      <Navigate
        to="/maintenance/artist"
        replace
        state={{ from: location, maintenance, role: 'artist' }}
      />
    );
  }
  return <ArtistOnboardingGate>{children}</ArtistOnboardingGate>;
};

// 未定義ルートの戻り先を決める。エリア接頭辞（/admin・/artist）を優先し、
// 判別できない場合はログイン中セッションのダッシュボード、どちらもなければトップへ。
const resolveFallbackDashboard = ({ pathname, adminUser, artistUser }) => {
  const path = String(pathname || '');
  if (path.startsWith('/admin')) return resolveAdminHomePath(adminUser);
  if (path.startsWith('/artist')) return ARTIST_DASHBOARD_PATH;
  if (adminUser) return resolveAdminHomePath(adminUser);
  if (artistUser) return ARTIST_DASHBOARD_PATH;
  return '/';
};

// どのルートにも一致しない（未定義）パスの受け皿。ダッシュボードへ自動遷移し通知を出す。
const NotFoundRedirect = () => {
  const { user: adminUser, checking: adminChecking } = useAuth();
  const { artistUser, checking: artistChecking } = useArtistAuth();
  const location = useLocation();
  const path = location.pathname || '';
  const isScopedArea = path.startsWith('/admin') || path.startsWith('/artist');

  // 不明なトップレベルパスは、セッション確定を待ってから振り分ける
  // （ログイン中は各自のダッシュボード、未ログインはトップへ）。
  if (!isScopedArea && (adminChecking || artistChecking)) {
    return <AuthGateScreen />;
  }

  const target = resolveFallbackDashboard({ pathname: path, adminUser, artistUser });
  return (
    <RedirectWithNotice
      to={target}
      toastType="info"
      message="お探しのページが見つかりませんでした。ダッシュボードに移動しました。"
    />
  );
};

const DASHBOARD_PATH_PREFIXES = [
  '/admin/workspaces',
  '/admin/settings',
  '/artist/workspaces',
  '/artist/settings',
];
const CHAT_PATH_KEYWORDS = ['/chat', '/artist-chat', '/event-chat', '/group-chat'];
const SERVICE_AI_CHAT_PATHS = new Set([
  '/admin/workspaces/service-ai',
  '/admin/workspaces/planning/ai-planner',
]);
const PAGINATION_SELECTOR = '.pagination';
const SCROLLABLE_OVERFLOWS = new Set(['auto', 'scroll', 'overlay']);

const findScrollableAncestor = (start) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  let node = start instanceof HTMLElement ? start : start?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const overflowY = (style && (style.overflowY || style.overflow)) || '';
    if (SCROLLABLE_OVERFLOWS.has(overflowY)) return node;
    node = node.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

const scrollContainerToTop = (start) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const container = findScrollableAncestor(start);
  if (!container) return;
  if (container === document.body || container === document.documentElement) {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return;
  }
  if (typeof container.scrollTo === 'function') {
    container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  } else {
    container.scrollTop = 0;
    container.scrollLeft = 0;
  }
};

// Body portals are React-owned too; route/logout cleanup belongs to their components.
const AppViewport = ({ guardAdmin, guardAdminPermission, guardArtist }) => {
  const location = useLocation();
  const isDashboardPath = DASHBOARD_PATH_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );
  const isLocalAdminUiMock = process.env.NODE_ENV !== 'production' && location.pathname === '/admin/mock/digital-survey-onboarding';
  const isLocalRespondentUiMock = process.env.NODE_ENV !== 'production' && location.pathname.startsWith('/surveys/mock-digital-onboarding/');
  const isAdminTaskWorkspacePath = location.pathname.startsWith('/admin/workspaces/task-management/');
  const isDashboardHomePath = location.pathname.endsWith('/dashboard');
  const isTimetableView = location.pathname.includes('/timetables/');
  const isChatPath =
    SERVICE_AI_CHAT_PATHS.has(location.pathname) ||
    CHAT_PATH_KEYWORDS.some((keyword) => location.pathname.includes(keyword));
  const layoutMode = isDashboardPath || isLocalAdminUiMock ? (isChatPath ? 'chat' : 'dashboard') : 'public';
  const resolveChatListOnly = useCallback(() => {
    if (!isChatPath) return true;
    if (location?.state?.reset) return true;
    if (typeof window === 'undefined') return true;
    const path = location.pathname || '';
    const search = location.search || '';

    if (SERVICE_AI_CHAT_PATHS.has(path)) {
      try {
        return !new URLSearchParams(search || '').get('session');
      } catch {
        return true;
      }
    }

    if (path.startsWith('/artist/workspaces/communications/chat')) return true;

    if (path.startsWith('/admin/workspaces/communications/artist-chat')) {
      try {
        const params = new URLSearchParams(search || '');
        if (params.get('artistId') || params.get('artist') || params.get('artist_id')) return false;
        if (sessionStorage.getItem('selectedArtistId')) return false;
        const last = sessionStorage.getItem('admin-artistchat:last');
        if (last) {
          const parsed = JSON.parse(last);
          if (parsed?.artistId) return false;
        }
      } catch {}
      return true;
    }

    if (path.startsWith('/admin/workspaces/event-operations/event-chat')) {
      try {
        const params = new URLSearchParams(search || '');
        if (params.get('event') || params.get('thread')) return false;
        const last =
          sessionStorage.getItem('admin-eventchat:last') ||
          localStorage.getItem('admin-eventchat:last');
        if (last) {
          const parsed = JSON.parse(last);
          if (parsed?.eventId) return false;
        }
      } catch {}
      return true;
    }

    if (path.startsWith('/admin/workspaces/communications/group-chat')) {
      try {
        const params = new URLSearchParams(search || '');
        if (params.get('thread')) return false;
      } catch {}
      return true;
    }

    if (path.startsWith('/artist/workspaces/event-operations/event-chat')) {
      try {
        const params = new URLSearchParams(search || '');
        if (params.get('event')) return false;
        const last = sessionStorage.getItem('artist-eventchat:last');
        if (last) {
          const parsed = JSON.parse(last);
          if (parsed?.eventId) return false;
        }
      } catch {}
      return true;
    }

    return true;
  }, [isChatPath, location.pathname, location.search, location.state]);
  const resolveChatFooterMode = useCallback(() => {
    const path = location.pathname || '';
    if (path.startsWith('/admin/workspaces/communications/group-chat')) return 'group';
    if (path.startsWith('/admin/workspaces/communications/artist-chat')) return 'artist';
    if (path.startsWith('/admin/workspaces/event-operations/event-chat')) return 'event';
    if (path.startsWith('/artist/workspaces/event-operations/event-chat')) return 'event';
    if (path.startsWith('/artist/workspaces/communications/chat')) return 'artist';
    return null;
  }, [location.pathname]);
  const [chatListOnly, setChatListOnly] = useState(() => resolveChatListOnly());
  const [chatFooterMode, setChatFooterMode] = useState(() => resolveChatFooterMode());
  const shouldShowFooterNav =
    isDashboardPath &&
    !isAdminTaskWorkspacePath &&
    !isTimetableView &&
    (!isChatPath || chatListOnly);

  const shouldShowAnnouncement = isDashboardPath && !isTimetableView;
  const isPublicSurveyPath = location.pathname.startsWith('/surveys/');
  const shouldShowFooter = !isDashboardPath && !isPublicSurveyPath && !isLocalAdminUiMock && !isLocalRespondentUiMock;

  // SP: チャットで会話を開いている間はグローバルヘッダーを隠して縦領域を確保する
  // （フッターナビ非表示と同じ isChatPath && !chatListOnly 条件。実際の非表示は
  //   Header.css の @media(max-width:700px) が担い、PCでは常に表示される）
  const isChatConversationActive = isChatPath && !chatListOnly;
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.toggle('chat-conversation-active', isChatConversationActive);
    return () => document.body.classList.remove('chat-conversation-active');
  }, [isChatConversationActive]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    if (!html || !body) return;
    if (isDashboardPath) {
      html.classList.remove('public-scroll');
      html.classList.remove('home-scroll');
      body.classList.remove('public-scroll');
      body.classList.remove('home-scroll');
      root?.classList.remove('public-scroll');
      root?.classList.remove('home-scroll');
    } else {
      body.classList.remove('sidebar-mobile-open');
      html.classList.add('public-scroll');
      body.classList.add('public-scroll');
      root?.classList.add('public-scroll');
    }
    return () => {
      html.classList.remove('public-scroll');
      html.classList.remove('home-scroll');
      body.classList.remove('public-scroll');
      body.classList.remove('home-scroll');
      root?.classList.remove('public-scroll');
      root?.classList.remove('home-scroll');
    };
  }, [isDashboardPath]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handlePaginationClick = (event) => {
      const target = event?.target;
      if (!(target instanceof Element)) return;
      const paginationRoot = target.closest(PAGINATION_SELECTOR);
      if (!paginationRoot) return;
      const trigger = target.closest('button, a');
      if (!trigger || !paginationRoot.contains(trigger)) return;
      if (trigger.matches('[disabled], [aria-disabled="true"], .disabled, .is-disabled')) return;
      scrollContainerToTop(paginationRoot);
    };
    document.addEventListener('click', handlePaginationClick);
    return () => document.removeEventListener('click', handlePaginationClick);
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleChatFooter = (event) => {
      const next = Boolean(event?.detail?.listOnly);
      setChatListOnly(next);
      const nextMode = String(event?.detail?.chatMode || '').trim();
      if (nextMode) setChatFooterMode(nextMode);
    };
    window.addEventListener('casticart:chat-footer', handleChatFooter);
    return () => window.removeEventListener('casticart:chat-footer', handleChatFooter);
  }, []);

  useLayoutEffect(() => {
    const next = resolveChatListOnly();
    setChatListOnly((prev) => (prev === next ? prev : next));
    const nextMode = resolveChatFooterMode();
    setChatFooterMode((prev) => (prev === nextMode ? prev : nextMode));
  }, [resolveChatFooterMode, resolveChatListOnly]);

  const routes = (
    <Routes>
      {/* ===== Global maintenance routes ===== */}
      <Route path="/maintenance" element={<MaintenanceNotice />} />
      <Route path="/maintenance/admin" element={<MaintenanceNotice />} />
      <Route path="/maintenance/artist" element={<MaintenanceNotice />} />
      <Route path="/dev/*" element={<DevPortalApp />} />
      <Route path="/" element={<HomePage />} />
      <Route path="/terms" element={<PolicyPage presetKey="terms" />} />
      <Route path="/privacy" element={<PolicyPage presetKey="privacy" />} />
      <Route path="/policies/:policyKey" element={<PolicyPage />} />
      <Route path="/concierge/apply" element={<ConciergeExternalApplyPage />} />
      <Route path="/concierge/status/:token" element={<ConciergeStatusPage />} />
      <Route path="/guide" element={<ServiceGuidePage />} />
      <Route path="/stage-preview/:token" element={<PublicStagePreviewPage />} />
      {process.env.NODE_ENV !== 'production' ? <Route path="/surveys/mock-digital-onboarding/:publicId" element={<DigitalSurveyOnboardingMockPage mode="respondent" />} /> : null}
      <Route path="/surveys/:publicId" element={<PublicSurveyPage />} />
      {/* ===== Login / Setup ===== */}
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/admin-setup/:token" element={<AdminSetup />} />
      <Route path="/artist-login" element={<ArtistLogin />} />
      <Route path="/artist-setup/:artistId" element={<ArtistSetup />} />

      <Route path="/artist-limited-invite/:token" element={<ArtistLimitedInviteGate />} />
      <Route path="/artist-signup/:artistId" element={<ArtistSignupFull />} />
  
      <Route path="/admin-forgot" element={<AdminForgotPassword />} />
      <Route path="/admin-reset/:token" element={<AdminResetPassword />} />
      <Route path="/artist-forgot-password" element={<ArtistForgotPassword />} />
      <Route path="/artist-reset-password/:token" element={<ArtistResetPassword />} />
      <Route path="/artist-email-change/confirm/:token" element={<ArtistEmailChangeConfirm />} />

      {/* ===== Admin ===== */}
      {process.env.NODE_ENV !== 'production' ? <Route path="/admin/mock/digital-survey-onboarding" element={<DigitalSurveyOnboardingMockPage mode="admin" />} /> : null}
      <Route path="/admin/workspaces/event-operations/dashboard" element={guardAdmin(<AdminDashboard />)} />
      <Route path="/admin/workspaces/system/announcements" element={guardAdminPermission(<AnnouncementManagement />, 'NAV_ANNOUNCEMENTS')} />
      <Route path="/admin/workspaces/system/operational-emails" element={guardAdminPermission(<AdminOperationalEmailPage />, 'NAV_OPERATIONAL_EMAILS')} />
      <Route path="/admin/workspaces/system/bugs" element={guardAdminPermission(<AdminBugManagement />, 'NAV_BUGS')} />
      <Route path="/admin/workspaces/system/account-management" element={guardAdminPermission(<AdminAccountManagement />, 'SYSTEM_MANAGE_ACCOUNTS')} />
      <Route path="/admin/settings/profile" element={guardAdmin(<AdminProfileEdit />)} />
      <Route path="/admin/settings/change-password" element={guardAdmin(<AdminChangePassword />)} />
      <Route path="/admin/settings/notifications" element={guardAdmin(<AdminNotificationSettings />)} />
      <Route path="/admin/settings/work-availability" element={guardAdmin(<AdminWorkAvailabilityPage />)} />
      <Route path="/admin/workspaces/system/manuals" element={guardAdminPermission(<AdminManualsPage />, 'SYSTEM_VIEW_MANUALS')} />
      <Route path="/admin/workspaces/system/manuals/:slug" element={guardAdminPermission(<AdminManualsPage />, 'SYSTEM_VIEW_MANUALS')} />
      <Route
        path="/admin/workspaces/communications/artist-chat"
        element={guardAdminPermission(<AdminChat />, 'NAV_PERSONAL_CHAT')}
      />
      <Route path="/admin/workspaces/communications/group-chat" element={guardAdminPermission(<AdminGroupChatPage />, 'NAV_GROUP_CHAT')} />
      <Route
        path="/admin/workspaces/communications/artist-chat/broadcast"
        element={guardAdminPermission(<AdminArtistBroadcastPage />, null, {
          permissionGroups: [['NAV_CHAT_BROADCAST'], ['ARTIST_VIEW_LIST', 'NAV_ARTISTS']],
        })}
      />
      <Route
        path="/admin/workspaces/finance/dashboard"
        element={guardAdminPermission(<AdminFinanceDashboard />, 'NAV_FINANCE')}
      />
      <Route path="/admin/workspaces/service-ai" element={guardAdminPermission(<AdminServiceAiPage />, 'NAV_AI_PLANNER')} />
      <Route path="/admin/workspaces/planning/ai-planner" element={guardAdminPermission(<AdminServiceAiPage />, 'NAV_AI_PLANNER')} />
      <Route
        path="/admin/workspaces/system/service-ai-settings"
        element={guardAdmin(<RequireExactAdminRole><AdminServiceAiSettingsPage /></RequireExactAdminRole>)}
      />
      <Route
        path="/admin/workspaces/system/contract-clause-settings"
        element={guardAdmin(<RequireExactAdminRole><ContractClauseSettingsPage /></RequireExactAdminRole>)}
      />
      <Route
        path="/admin/workspaces/system/service-ai-settings/conversations"
        element={guardAdmin(<RequireExactAdminRole><AdminServiceAiConversationReviewPage /></RequireExactAdminRole>)}
      />
      <Route
        path="/admin/workspaces/system/service-ai-settings/usage-logs"
        element={guardAdmin(<RequireExactAdminRole><AdminServiceAiUsageLogPage /></RequireExactAdminRole>)}
      />
      <Route path="/admin/workspaces/task-management/dashboard" element={guardAdminPermission(<AdminTaskHome />, 'NAV_TASKS')} />
      <Route path="/admin/workspaces/task-management/events" element={guardAdminPermission(<AdminTaskEventList />, 'NAV_TASKS')} />
      <Route path="/admin/workspaces/task-management/quick-tasks" element={guardAdminPermission(<AdminQuickTaskListPage />, 'NAV_TASKS')} />
      <Route
        path="/admin/workspaces/task-management/templates/new"
        element={guardAdminPermission(<AdminTaskTemplateCreatePage />, 'NAV_TASKS')}
      />
      <Route path="/admin/workspaces/task-management/projects/:projectId" element={guardAdminPermission(<AdminTaskProjectDetail />, 'NAV_TASKS')} />
      <Route path="/admin/workspaces/system/policies" element={guardAdminPermission(<PolicyEditor />, 'CAN_EDIT_POLICIES')} />
      <Route path="/admin/workspaces/system/mobile-release" element={guardAdminPermission(<MobileReleaseManagement />, 'SYSTEM_MANAGE_MOBILE_RELEASES')} />
      <Route path="/admin/workspaces/system/mac-release" element={guardAdminPermission(<MacReleaseManagement />, 'SYSTEM_MANAGE_MAC_RELEASES')} />
      <Route path="/admin/workspaces/system/action-logs" element={guardAdminPermission(<AdminActionLogPage />, 'CAN_VIEW_ACTION_LOGS')} />
      <Route
        path="/admin/workspaces/system/playguide-connections"
        element={guardAdmin(<RequireExactAdminRole><PlayguideConnectionSettings /></RequireExactAdminRole>)}
      />

      {/* Admin > Artists */}
      <Route path="/admin/workspaces/artist-management/artists/register" element={guardAdminPermission(<ArtistRegister />, 'NAV_ARTISTS')} />
      <Route path="/admin/workspaces/artist-management/artists/registration-complete" element={guardAdminPermission(<ArtistRegistrationComplete />, 'NAV_ARTISTS')} />
      <Route
        path="/admin/workspaces/artist-management/artists"
        element={guardAdminPermission(<ArtistList />, ['NAV_ARTISTS', 'ARTIST_VIEW_LIST'])}
      />
      <Route path="/admin/workspaces/artist-management/groups" element={guardAdminPermission(<ArtistGroups />, 'NAV_ARTISTS')} />
      <Route
        path="/admin/workspaces/artist-management/artists/:artistId"
        element={guardAdminPermission(<ArtistDetail />, ['NAV_ARTISTS', 'ARTIST_VIEW_PROFILE_BASIC'])}
      />

      {/* Admin > Events */}
      <Route path="/admin/workspaces/event-operations/events/register" element={guardAdminPermission(<EventRegistration />, 'NAV_EVENT_REGISTER')} />
      <Route path="/admin/workspaces/event-operations/events/registration-complete" element={guardAdminPermission(<EventRegistrationComplete />, 'NAV_EVENT_REGISTER')} />
      <Route
        path="/admin/workspaces/event-operations/events"
        element={guardAdminPermission(<EventList />, ['NAV_EVENTS', 'EVENT_VIEW_PUBLISHED_LIST', 'EVENT_VIEW_UNPUBLISHED_LIST'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId"
        element={guardAdminPermission(<EventDetails />, ['NAV_EVENTS', 'EVENT_VIEW_PUBLISHED_DETAIL', 'EVENT_VIEW_UNPUBLISHED_DETAIL'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/surveys"
        element={guardAdminPermission(<AdminSurveyImportsPage />, null, {
          permissionGroups: [
            ['NAV_SURVEYS'],
            ['NAV_EVENTS', 'EVENT_VIEW_PUBLISHED_DETAIL', 'EVENT_VIEW_UNPUBLISHED_DETAIL'],
          ],
        })}
      />
      <Route path="/admin/workspaces/event-operations/events/:eventId/casting-registration" element={guardAdminPermission(<EventCastingRegistration />, 'NAV_EVENTS')} />
      <Route path="/admin/workspaces/event-operations/events/:eventId/edit" element={guardAdminPermission(<EventEdit />, 'NAV_EVENTS')} />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/casting-workbench"
        element={guardAdminPermission(<EventCastingWorkbench />, null, {permissionGroups: [['ARTIST_VIEW_LIST'], ['ARTIST_VIEW_PROFILE_BASIC'], ['EVENT_VIEW_PARTICIPANTS'], ['EVENT_VIEW_CONTRACT_STATUS'], ['NAV_CASTING_OFFER'], ['NAV_CASTING_CONTRACT']]})}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/casting-progress"
        element={guardAdminPermission(<CastingProgress />, ['NAV_EVENTS', 'EVENT_VIEW_PARTICIPANTS', 'EVENT_VIEW_CONTRACT_STATUS'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/ticket-sales"
        element={guardAdminPermission(<EventTicketSales />, ['NAV_TICKET_SALES', 'EVENT_VIEW_TICKET_SALES'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/ticket-sales/analytics"
        element={guardAdminPermission(<EventTicketSalesAnalytics />, ['NAV_TICKET_SALES', 'EVENT_VIEW_TICKET_SALES'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/marketing"
        element={guardAdminPermission(<AdminEventMarketingPage />, 'EVENT_VIEW_MARKETING')}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/pl"
        element={guardAdminPermission(<EventProfitLoss />, 'NAV_FINANCE')}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/timetable"
        element={guardAdminPermission(<EventTimetableDesigner />, ['NAV_EVENTS', 'EVENT_VIEW_TIMETABLE', 'EVENT_EDIT_TIMETABLE'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/timetables/:timetableId"
        element={guardAdminPermission(<TimetableViewerPage mode="admin" />, ['NAV_EVENTS', 'EVENT_VIEW_TIMETABLE'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/participant-artists"
        element={guardAdminPermission(<EventParticipantInfo />, ['NAV_EVENTS', 'EVENT_VIEW_PARTICIPANTS'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/stage-seating"
        element={guardAdminPermission(<EventStageSeatingPlanner />, ['NAV_EVENTS', 'EVENT_VIEW_PARTICIPANTS'])}
      />
      <Route
        path="/admin/workspaces/event-operations/events/:eventId/stage-seating/preview"
        element={guardAdminPermission(<EventStageSeatingPlanner initialMode="preview" />, ['NAV_EVENTS', 'EVENT_VIEW_PARTICIPANTS'])}
      />
      <Route
        path="/admin/workspaces/event-operations/ticket-sales"
        element={guardAdminPermission(<TicketSalesOverview />, ['NAV_TICKET_SALES', 'EVENT_VIEW_TICKET_SALES'])}
      />
      <Route path="/admin/workspaces/event-operations/event-chat" element={guardAdminPermission(<EventChatPage />, 'NAV_EVENT_CHAT')} />
      <Route path="/admin/workspaces/event-operations/event-options" element={guardAdminPermission(<EventOptionEdit />, 'NAV_EVENTS')} />
      <Route path="/admin/workspaces/event-operations/genres" element={guardAdminPermission(<EventGenreManagement />, 'NAV_EVENTS')} />
      <Route
        path="/admin/workspaces/library/music"
        element={guardAdminPermission(<MusicLibraryManagement />, null, {
          permissionGroups: [['NAV_MUSIC_LIBRARY'], ['MUSIC_VIEW_LIBRARY']],
        })}
      />

      {/* Admin > Venues */}
      <Route path="/admin/workspaces/event-operations/venues" element={guardAdminPermission(<VenueList />, 'NAV_VENUES')} />
      <Route path="/admin/workspaces/event-operations/venues/register" element={guardAdminPermission(<VenueRegistration />, 'NAV_VENUES')} />
      <Route path="/admin/workspaces/event-operations/venues/:venueId" element={guardAdminPermission(<VenueDetail />, 'NAV_VENUES')} />

      {/* Admin > Casting (Hold) */}
      <Route path="/admin/workspaces/casting/offers/new" element={guardAdminPermission(<HoldCasting />, 'NAV_CASTING_OFFER')} />
      <Route path="/admin/workspaces/casting/offers" element={guardAdminPermission(<HoldCastingHistory />, 'NAV_CASTING_OFFER')} />
      <Route path="/admin/workspaces/casting/offers/:holdCastingId" element={guardAdminPermission(<HoldCastingDetail />, 'NAV_CASTING_OFFER')} />
      <Route path="/admin/workspaces/casting/offers/by-hold/:holdCastingId" element={guardAdminPermission(<HoldCastingDetail />, 'NAV_CASTING_OFFER')} />

      {/* Admin > Contracts */}
      <Route path="/admin/workspaces/contracts/form/:holdCastingId?" element={guardAdminPermission(<ContractForm />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/confirm" element={guardAdminPermission(<ContractConfirm />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/success" element={guardAdminPermission(<ContractSuccess />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/history" element={guardAdminPermission(<ContractHistory />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/events/:eventId/additional-rewards" element={guardAdminPermission(<AdditionalRewardBatchPage />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/events/:eventId/schedules" element={guardAdminPermission(<EventContractScheduleBatchPage />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/:contractArtistId" element={guardAdminPermission(<ContractDetail />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/:contractArtistId/schedule-edit" element={guardAdminPermission(<ContractEdit scheduleOnly />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/:contractArtistId/edit" element={guardAdminPermission(<ContractEdit />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/:contractArtistId/edit/confirm/personal" element={guardAdminPermission(<ContractEditConfirmPersonal />, 'NAV_CASTING_CONTRACT')} />
      <Route path="/admin/workspaces/contracts/:contractArtistId/edit/confirm/common" element={guardAdminPermission(<ContractEditConfirmCommon />, 'NAV_CASTING_CONTRACT')} />

      {/* Admin > Others */}
      <Route path="/admin/workspaces/artist-management/parts" element={guardAdmin(<PartsEdit />)} />
      <Route path="/admin/workspaces/artist-management/group-tags" element={guardAdmin(<GroupTagEdit />)} />
      <Route
        path="/admin/workspaces/finance/payments/monthly"
        element={guardAdminPermission(<MonthlyPaymentsList />, 'NAV_PAYMENTS')}
      />
      <Route
        path="/admin/workspaces/finance/payments/invoices/:contractArtistId"
        element={guardAdminPermission(<AdminInvoicePreview />, 'NAV_PAYMENTS')}
      />
      <Route
        path="/admin/payments"
        element={guardAdmin(<Navigate to="/admin/workspaces/finance/payments/monthly" replace />)}
      />
      <Route path="/admin/workspaces/concierge/dashboard" element={guardAdminPermission(<AdminConciergeList />, 'CAN_MANAGE_CONCIERGE')} />

      {/* ===== Artist ===== */}
      <Route path="/artist/workspaces/event-operations/dashboard" element={guardArtist(<ArtistDashboard />)} />
      <Route path="/artist/workspaces/event-operations/events" element={guardArtist(<ArtistEventList />)} />
      <Route path="/artist/workspaces/event-operations/events/:eventId" element={guardArtist(<ArtistEventDetails />)} />
      <Route path="/artist/events/:eventUuid/guest-tickets" element={guardArtist(<ArtistGuestTicketsPage />)} />
      <Route path="/artist/workspaces/event-operations/events/:eventUuid/guest-tickets" element={guardArtist(<ArtistGuestTicketsPage />)} />
      <Route
        path="/artist/workspaces/event-operations/events/:eventId/stage-seating"
        element={guardArtist(<EventStageSeatingPlanner viewer="artist" />)}
      />
      <Route
        path="/artist/workspaces/event-operations/events/:eventId/stage-seating/preview"
        element={guardArtist(<EventStageSeatingPlanner initialMode="preview" viewer="artist" />)}
      />
      <Route
        path="/artist/workspaces/event-operations/events/:eventId/timetables/:timetableId"
        element={guardArtist(<TimetableViewerPage mode="artist" />)}
      />
      <Route path="/artist/workspaces/event-operations/event-chat" element={guardArtist(<ArtistEventChatPage />)} />
      <Route path="/artist/workspaces/communications/chat" element={guardArtist(<ArtistChat />)} />
      <Route path="/artist/workspaces/casting/offers" element={guardArtist(<ArtistOffers />)} />
      <Route path="/artist/workspaces/casting/offers/:offerID" element={guardArtist(<ArtistOfferDetail />)} />
      <Route path="/artist/workspaces/contracts/dashboard" element={guardArtist(<ArtistContractHistory />)} />
      <Route path="/artist/workspaces/contracts/:contractArtistId" element={guardArtist(<ArtistContractDetail />)} />
      <Route path="/artist/workspaces/contracts/:contractArtistId/print" element={guardArtist(<PrintContract />)} />
      <Route path="/artist/workspaces/event-operations/venues/:venueId" element={guardArtist(<ArtistVenueDetail />)} />
      <Route path="/artist/settings/profile" element={guardArtist(<ArtistProfileEdit />)} />
      <Route path="/artist/workspaces/support/manuals" element={guardArtist(<ArtistManualsPage />)} />
      <Route path="/artist/workspaces/support/manuals/:slug" element={guardArtist(<ArtistManualsPage />)} />
      <Route path="/artist/workspaces/concierge/dashboard" element={guardArtist(<ArtistConciergeDashboard />)} />
      <Route path="/artist/workspaces/concierge/apply" element={guardArtist(<ArtistConciergeApplyForm />)} />
      <Route
        path="/artist/workspaces/concierge/additional-info/:token"
        element={guardArtist(<ArtistConciergeAdditionalInfoPage />)}
      />
      <Route path="/artist/settings/email-change" element={guardArtist(<ArtistEmailChange />)} />
      <Route path="/artist/workspaces/support/bug-reports" element={guardArtist(<ArtistBugReportPage />)} />
      <Route path="/artist/settings/bank-info" element={guardArtist(<ArtistBankInfo />)} />
      <Route path="/artist/settings/notifications" element={guardArtist(<ArtistNotificationSettings />)} />
      <Route path="/artist/settings/change-password" element={guardArtist(<ArtistChangePassword />)} />
      <Route path="/artist/settings/artist-photography" element={guardArtist(<ArtistPhotography />)} />
      <Route path="/artist/workspaces/finance/rewards" element={guardArtist(<ArtistRewardsList />)} />
      <Route path="/artist/workspaces/finance/invoice-maker" element={guardArtist(<ArtistInvoiceMakerPage />)} />

      {/* ===== 未定義ルートの受け皿（ダッシュボードへ自動遷移＋通知） ===== */}
      <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
  );

  const wrappedRoutes = (
    <Suspense fallback={<AuthGateScreen />}>
      {routes}
      {shouldShowFooterNav && <ResponsiveFooterNav chatMode={chatFooterMode} />}
    </Suspense>
  );

  return (
    <div className={`surface surface--${layoutMode}${shouldShowFooterNav ? ' surface--footer-nav' : ''}`}>
      <QueryRedirect />
      <OpenInAppBanner enabled={isDashboardHomePath} />
      {wrappedRoutes}
      {shouldShowAnnouncement && <AnnouncementPopup />}
      {shouldShowFooter && <SiteFooter />}
    </div>
  );
};

function App() {
  useSelectionSafeNavigation();
  const isLocalUiMock = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && (
    window.location.pathname === '/admin/mock/digital-survey-onboarding' ||
    window.location.pathname.startsWith('/surveys/mock-digital-onboarding/')
  );
  const emitSidebarVisibility = (visible) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('casticart:sidebar-visibility', { detail: { visible } }));
  };

  const closeSidebarOverlay = () => {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('sidebar-mobile-open');
      emitSidebarVisibility(false);
    }
  };

  const guardAdmin = (element) => <RequireAdminAuth>{element}</RequireAdminAuth>;
  const guardAdminPermission = (element, permissionKeys, options = {}) => (
    <RequireAdminAuth>
      <RequireAdminPermission
        permissionKeys={permissionKeys}
        permissionGroups={options.permissionGroups}
        mode={options.mode || 'any'}
      >
        {element}
      </RequireAdminPermission>
    </RequireAdminAuth>
  );
  const guardArtist = (element) => <RequireArtistAuth>{element}</RequireArtistAuth>;

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <OfferCountProvider>
      <ContractCountProvider>
      <AdminMenuBadgeProvider>
      <MessageCountProvider>
        <ResponseStatusProvider>
        <PdfPreviewProvider>
          {!isLocalUiMock ? <GlobalSocketListener /> : null}
          {!isLocalUiMock ? <ArtistDemoBanner /> : null}
          {!isLocalUiMock ? <Header /> : null}
          {!isLocalUiMock ? <AdminFullNameRequiredModal /> : null}
          {!isLocalUiMock ? <AdminWorkAvailabilityPrompt /> : null}
          {!isLocalUiMock ? <MaintenanceBanner /> : null}
          {!isLocalUiMock ? <ConnectivityBanner /> : null}
          <main id="app-shell" role="main" aria-label="Application Content">
            <div
              className="sidebar-mobile-overlay"
              aria-hidden="true"
              onClick={closeSidebarOverlay}
            />
            <div className="app-shell__viewport">
              <AppViewport guardAdmin={guardAdmin} guardAdminPermission={guardAdminPermission} guardArtist={guardArtist} />
              {!isLocalUiMock ? <AdminQuickTaskGlobalLauncher /> : null}
            </div>
          </main>
          {!isLocalUiMock ? <PeerToastStack /> : null}
        </PdfPreviewProvider>
        </ResponseStatusProvider>
      </MessageCountProvider>
      </AdminMenuBadgeProvider>
      </ContractCountProvider>
      </OfferCountProvider>
    </Router>
  );
}

export default App;
